import { useId, useRef } from 'react'
import styles from './Champ.module.css'

const LONGUEUR = 6

interface ProprietesChampCode {
  readonly libelle: string
  readonly valeur: string
  readonly onChange: (valeur: string) => void
  readonly aide?: string | undefined
  readonly erreur?: string | undefined
  readonly disabled?: boolean
  readonly autoFocus?: boolean
}

/**
 * Saisie d'un code à six chiffres — variante du composant `Champ`, pas un
 * composant de plus dans l'inventaire (skill `design-system-afs`).
 *
 * Un seul `<input>` réel, six cases dessinées derrière lui. Six champs
 * séparés — la tentation naturelle devant cette maquette — cassent le collage
 * depuis le presse-papiers, la navigation clavier et la restitution par
 * lecteur d'écran. C'est précisément ce qu'un utilisateur pressé fait d'un
 * code à usage unique : il le colle.
 */
export function ChampCode({
  libelle,
  valeur,
  onChange,
  aide,
  erreur,
  disabled = false,
  autoFocus = false,
}: ProprietesChampCode): React.JSX.Element {
  const id = useId()
  const idAide = `${id}-aide`
  const idErreur = `${id}-erreur`
  const saisie = useRef<HTMLInputElement>(null)

  const decrit = [aide ? idAide : '', erreur ? idErreur : ''].filter(Boolean).join(' ')
  const cases = Array.from({ length: LONGUEUR }, (_, index) => valeur[index] ?? '')

  return (
    <div className={styles.groupe}>
      <label htmlFor={id} className={styles.libelle}>
        {libelle}
      </label>

      <div className={styles.zoneCode} onClick={() => saisie.current?.focus()} role="presentation">
        <div className={styles.cases} aria-hidden="true">
          {cases.map((chiffre, index) => (
            <div
              key={index}
              className={`${styles.case} ${
                index === valeur.length && !disabled ? styles.caseActive : ''
              }`}
            >
              {chiffre ||
                (index === valeur.length && !disabled ? <span className={styles.curseur} /> : null)}
            </div>
          ))}
        </div>

        <input
          ref={saisie}
          id={id}
          className={styles.saisieCode}
          value={valeur}
          disabled={disabled}
          autoFocus={autoFocus}
          // `inputMode` fait sortir le pavé numérique sur tablette — les
          // agences en utilisent (CCT § 3.3).
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={LONGUEUR}
          aria-invalid={erreur ? true : undefined}
          aria-describedby={decrit || undefined}
          onChange={(evenement) => {
            onChange(evenement.target.value.replace(/\D/g, '').slice(0, LONGUEUR))
          }}
        />
      </div>

      {aide && !erreur && (
        <span id={idAide} className={styles.aide}>
          {aide}
        </span>
      )}

      {erreur && (
        <span id={idErreur} className={styles.erreur} role="alert">
          {erreur}
        </span>
      )}
    </div>
  )
}
